import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';

import { Client } from 'src/app/models';
import { ENV_CONFIG } from 'src/bin/env.config';
import { ErrorHandlerService } from './error-handler.service';
import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { Observable } from 'rxjs';
import { SalonmonsterHttpClient } from '../services/salonmonster-http-client';
import { UserService } from './user.service';
import { map } from 'rxjs/operators';

export interface BulkTextRecipient {
  clientID: number;
  phone: string;
  countryID: number;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class BulkTextService extends SalonmonsterHttpClient {
  constructor(
    http: HttpClient,
    userService: UserService,
    protected errorHandlerService: ErrorHandlerService
  ) {
    super(http, userService, errorHandlerService);
  }


  public sendToSelectedClients(
    stylistID: number,
    bulkText: string,
    selectedClients
  ): Observable<number> {
    const url = `${ENV_CONFIG.API_ROOT}/bulktext`;

    return this.post(url, {
      selectedClients: selectedClients,
      stylistID: stylistID,
      bulkText: bulkText
    }).pipe(
      map((data) => {
        return data.count;
      })
    );
  }

  public getSegmentCount(
    text: string    
  ): Observable<number> {
    const url = `${ENV_CONFIG.API_ROOT}/bulktext/segment`;
    return this.post(url, {
        text: text
    }).pipe(
      map((data) => {
        return data;
      })
    );
  }

  public stylistClientsRecipients(
    bulkText: string,
    stylistID: number
  ): Observable<List<BulkTextRecipient>> {
    const url = `${ENV_CONFIG.API_ROOT}/bulktext/recipients`;

    return this.post(
      url,
      this.buildStylistClientsFilterParams(
        bulkText,
        stylistID
      )
    ).pipe(
      map((data) => {
        let recipients: List<BulkTextRecipient> = List([]);

        for (const recipient of data) {
          recipients = recipients.push({
            clientID: recipient.clientID,
            name: recipient.name,
            phone: recipient.phone,
            countryID: recipient.countryID
          });
        }

        return recipients;
      })
    );
  }

  public clientsWhoseAppointmentsRecipients(
    bulkText: string,
    stylistID: number,
    beforeAfter: string,
    startDate: string,
    endDate: string
  ): Observable<List<BulkTextRecipient>> {
    const url = `${ENV_CONFIG.API_ROOT}/bulktext/recipients`;

    return this.post(
      url,
      this.buildAppointmentFilterParams(
        bulkText,
        stylistID,
        beforeAfter,
        startDate,
        endDate
      )
    ).pipe(
      map((data) => {
        let recipients: List<BulkTextRecipient> = List([]);

        for (const recipient of data) {
          recipients = recipients.push({
            clientID: recipient.clientID,
            name: recipient.name,
            phone: recipient.phone,
            countryID: recipient.countryID
          });
        }

        return recipients;
      })
    );
  }

  public clientsWhoSpentRecipients(
    bulkText: string,
    stylistID: number,
    type: string,
    startAmount: number,
    endAmount: number
  ): Observable<List<BulkTextRecipient>> {
    const url = `${ENV_CONFIG.API_ROOT}/bulktext/recipients`;

    return this.post(
      url,
      this.buildSpenfFilterParams(
        bulkText,
        stylistID,
        type,
        startAmount,
        endAmount
      )
    ).pipe(
      map((data) => {
        let recipients: List<BulkTextRecipient> = List([]);

        for (const recipient of data) {
          recipients = recipients.push({
            clientID: recipient.clientID,
            name: recipient.name,
            phone: recipient.phone,
            countryID: recipient.countryID
          });
        }

        return recipients;
      })
    );
  }

  public clientsWhoseBirthdayRecipients(
    bulkText: string,
    stylistID: number,
    type: string,
    startBirthDate: string,
    endBirthDate: string
  ): Observable<List<BulkTextRecipient>> {
    const url = `${ENV_CONFIG.API_ROOT}/bulktext/recipients`;

    return this.post(
      url,
      this.buildBirthdayFilter(
        bulkText,
        stylistID,
        type,
        startBirthDate,
        endBirthDate
      )
    ).pipe(
      map((data) => {
        let recipients: List<BulkTextRecipient> = List([]);

        for (const recipient of data) {
          recipients = recipients.push({
            clientID: recipient.clientID,
            name: recipient.name,
            phone: recipient.phone,
            countryID: recipient.countryID
          });
        }

        return recipients;
      })
    );
  }

  public clientsWhoHadServiceRecipients(
    bulkText: string,
    stylistID: number,
    serviceName: string,
  ): Observable<List<BulkTextRecipient>> {
    const url = `${ENV_CONFIG.API_ROOT}/bulktext/recipients`;

    return this.post(
      url,
      this.buildServiceFilterParams(
        bulkText,
        stylistID,
        serviceName
      )
    ).pipe(
      map((data) => {
        let recipients: List<BulkTextRecipient> = List([]);

        for (const recipient of data) {
          recipients = recipients.push({
            clientID: recipient.clientID,
            name: recipient.name,
            phone: recipient.phone,
            countryID: recipient.countryID
          });
        }

        return recipients;
      })
    );
  }

  private buildStylistClientsFilterParams(
    bulkText: string,
    stylistID: number
  ): any {
    return {
      filterType: 'allclients',
      bulkText,
      stylistID
    };
  }

  private buildAppointmentFilterParams(
    bulkText: string,
    stylistID: number,
    beforeAfter: string,
    startDate: string,
    endDate: string
  ): any {
    return {
      filterType: 'appointment',
      bulkText,
      stylistID,
      beforeAfter,
      startDate,
      endDate
    };
  }

  private buildSpenfFilterParams(
    bulkText: string,
    stylistID: number,
    type: string,
    startAmount: number,
    endAmount: number
  ): any {
    return {
      filterType: 'spent',
      bulkText,
      stylistID,
      type,
      startAmount,
      endAmount
    };
  }

  private buildBirthdayFilter(
    bulkText: string,
    stylistID: number,
    type: string,
    startBirthDate: string,
    endBirthDate: string
  ): any {
    return {
      filterType: 'birthday',
      bulkText,
      stylistID,
      type,
      startBirthDate,
      endBirthDate
    };
  }

  public buildServiceFilterParams(
    bulkText: string,
    stylistID: number,
    serviceName: string
  ): any {
    return {
      filterType: 'service',
      bulkText,
      stylistID,
      serviceName
    };
  }
}
